// src/services/messaging-service.js
// Firestore-backed messaging for practitioner → client conversations.
// Schema:
//   practitioners/{practitionerId}/conversations/{conversationId}
//   practitioners/{practitionerId}/conversations/{conversationId}/messages/{messageId}

import { db } from '../config/firebase-config';
import {
  collection,
  doc,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const REGION = 'us-west1';

function convsRef(practitionerId) {
  return collection(db, 'practitioners', practitionerId, 'conversations');
}

function msgsRef(practitionerId, conversationId) {
  return collection(db, 'practitioners', practitionerId, 'conversations', conversationId, 'messages');
}

// ---------------------------------------------------------------------------
// subscribeToConversations
// Returns an unsubscribe function. Calls callback(conversations[]) on every
// update. Conversations are ordered newest first.
// Each conversation shape:
//   { id, clientEmail, clientName, clientId, subject, lastMessageAt (Date),
//     lastMessagePreview, lastMessageBy, unreadCount, status, createdAt (Date) }
// ---------------------------------------------------------------------------
export function subscribeToConversations(practitionerId, callback) {
  const q = query(convsRef(practitionerId), orderBy('lastMessageAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const convs = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        clientEmail: data.clientEmail || '',
        clientName: data.clientName || 'Unknown',
        clientId: data.clientId || null,
        subject: data.subject || '(No subject)',
        lastMessageAt: data.lastMessageAt?.toDate() || new Date(),
        lastMessagePreview: data.lastMessagePreview || '',
        lastMessageBy: data.lastMessageBy || 'practitioner',
        unreadCount: data.unreadCount || 0,
        status: data.status || 'active',
        createdAt: data.createdAt?.toDate() || new Date(),
      };
    });
    callback(convs);
  });
}

// ---------------------------------------------------------------------------
// subscribeToMessages
// Returns an unsubscribe function. Calls callback(messages[]) on every update.
// Messages ordered oldest first (ascending sentAt).
// Each message shape:
//   { id, sender ('practitioner'|'client'), senderName, content, sentAt (Date),
//     readAt (Date|null), emailDelivered, emailMessageId, viaEmail }
// ---------------------------------------------------------------------------
export function subscribeToMessages(practitionerId, conversationId, callback) {
  const q = query(msgsRef(practitionerId, conversationId), orderBy('sentAt', 'asc'), limit(200));
  return onSnapshot(q, (snap) => {
    const msgs = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        sender: data.sender || 'practitioner',
        senderName: data.senderName || '',
        content: data.content || '',
        sentAt: data.sentAt?.toDate() || new Date(),
        readAt: data.readAt?.toDate() || null,
        emailDelivered: data.emailDelivered || false,
        emailMessageId: data.emailMessageId || null,
        viaEmail: data.sender === 'client', // client messages arrive via email
      };
    });
    callback(msgs);
  });
}

// ---------------------------------------------------------------------------
// sendMessage
// Calls the sendPractitionerMessage Cloud Function (callable).
// Adds message to Firestore + sends email to client.
// Returns { success: true } or throws.
// ---------------------------------------------------------------------------
export async function sendMessage(practitionerId, conversationId, content) {
  const functions = getFunctions(undefined, REGION);
  const fn = httpsCallable(functions, 'sendPractitionerMessage');
  const result = await fn({ practitionerId, conversationId, content });
  return result.data;
}

// ---------------------------------------------------------------------------
// markAsRead
// Sets unreadCount to 0 on the conversation doc.
// Safe to call whenever a conversation is opened.
// ---------------------------------------------------------------------------
export async function markAsRead(practitionerId, conversationId) {
  try {
    const convDoc = doc(db, 'practitioners', practitionerId, 'conversations', conversationId);
    await updateDoc(convDoc, { unreadCount: 0 });
  } catch (err) {
    console.warn('messaging-service: markAsRead failed (non-critical):', err.message);
  }
}

// ---------------------------------------------------------------------------
// archiveConversation
// Sets status to 'archived' on the conversation doc.
// ---------------------------------------------------------------------------
export async function archiveConversation(practitionerId, conversationId) {
  const convDoc = doc(db, 'practitioners', practitionerId, 'conversations', conversationId);
  await updateDoc(convDoc, { status: 'archived', archivedAt: serverTimestamp() });
}

// ---------------------------------------------------------------------------
// unarchiveConversation
// Restores an archived conversation to 'active'.
// ---------------------------------------------------------------------------
export async function unarchiveConversation(practitionerId, conversationId) {
  const convDoc = doc(db, 'practitioners', practitionerId, 'conversations', conversationId);
  await updateDoc(convDoc, { status: 'active', archivedAt: null });
}

// ---------------------------------------------------------------------------
// getTotalUnreadCount
// One-time snapshot of total unread messages across all conversations.
// Used by Layout.js for the nav badge.
// Returns a number.
// ---------------------------------------------------------------------------
export async function getTotalUnreadCount(practitionerId) {
  const { getDocs } = await import('firebase/firestore');
  const snap = await getDocs(convsRef(practitionerId));
  let total = 0;
  snap.forEach((d) => {
    total += d.data().unreadCount || 0;
  });
  return total;
}

// ---------------------------------------------------------------------------
// subscribeToUnreadCount
// Realtime listener that reports total unread across all conversations.
// Returns unsubscribe function.
// ---------------------------------------------------------------------------
export function subscribeToUnreadCount(practitionerId, callback) {
  return onSnapshot(convsRef(practitionerId), (snap) => {
    let total = 0;
    snap.forEach((d) => { total += d.data().unreadCount || 0; });
    callback(total);
  });
}
